import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { Sentry } from '@/util/sentry';

type WebStorageType = 'local' | 'session';

export interface AsyncKeyValueStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

const wrapWebStorage = (storage: Storage): AsyncKeyValueStorage => {
  return {
    async getItem(key: string) {
      return storage.getItem(key);
    },
    async setItem(key: string, value: string) {
      storage.setItem(key, value);
    },
    async removeItem(key: string) {
      storage.removeItem(key);
    },
  };
};

/**
 * Synchronous localStorage.getItem with safe fallback.
 * Returns null if localStorage is unavailable or access is blocked.
 */
export const safeLocalStorageGetItem = (key: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Synchronous localStorage.setItem with safe fallback.
 * No-ops if localStorage is unavailable or access is blocked.
 */
export const safeLocalStorageSetItem = (key: string, value: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch {}
};

/**
 * Returns an async storage interface based on platform and identity.
 * - Anonymous web users: web storage (default localStorage)
 * - Authenticated web or native: AsyncStorage
 */
export const getPlatformStorage = (
  userId?: string,
  webStorage: WebStorageType = 'local'
): AsyncKeyValueStorage => {
  const isAnonymousWeb = !userId && isWeb && typeof window !== 'undefined';

  if (isAnonymousWeb) {
    try {
      const storage = webStorage === 'local' ? window.localStorage : window.sessionStorage;

      if (storage) {
        return wrapWebStorage(storage);
      }
    } catch {}
  }

  return {
    getItem: (key: string) => AsyncStorage.getItem(key),
    setItem: (key: string, value: string) => AsyncStorage.setItem(key, value),
    removeItem: (key: string) => AsyncStorage.removeItem(key),
  };
};

/**
 * Runs an async op (typically a `getPlatformStorage()` read/write), catching
 * and reporting any failure to Sentry instead of throwing, so a storage
 * error degrades to a safe default rather than crashing the caller.
 */
export const safeStorageOp = async <T>(
  op: () => Promise<T>,
  tags?: Record<string, string>
): Promise<T | null> => {
  try {
    return await op();
  } catch (error) {
    Sentry.captureException(error, tags ? { tags } : undefined);

    return null;
  }
};
